import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TriangleAlert } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Modal } from '../../../../../components/ui/Modal';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import {
  changeOrderCourier,
  fetchAvailableCouriers,
} from '../../../../../features/orderDetails/orderDetailsThunks';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';

const ChangeCourierDialog = ({ onClose, order, onError }) => {
  const dispatch = useDispatch();
  const { couriers } = useSelector((state) => state.orderDetails);
  const loadingCouriers = useSelector(selectIsRunning(fetchAvailableCouriers));

  // Read once, at mount: a refetch of the order while it is open must not put
  // back the current courier over the one just picked.
  const [courier, setCourier] = useState(order.delivery?.shipping_zone?.courier || '');
  const [saving, setSaving] = useState(false);
  const [dispatched, setDispatched] = useState(null);

  useEffect(() => {
    dispatch(fetchAvailableCouriers());
  }, [dispatch]);

  const options = couriers.map((item) => ({ value: item.courier, label: item.courier }));
  // The list is asked for on opening: empty only means "none" once it answered.
  const emptyLabel = loadingCouriers ? 'Cargando couriers...' : 'Sin couriers disponibles';

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await dispatch(
        changeOrderCourier({ orderId: order.id, deliveryId: order.delivery.id, courier })
      ).unwrap();

      if (result.needsDispatchConfirmation) {
        setDispatched(courier);
        return;
      }
      onClose();
    } catch (error) {
      onError(error);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Cambiar Courier" busy={saving}>
      {dispatched ? (
        <div className="flex flex-col gap-6">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 rounded-xl p-4 flex gap-3">
            <TriangleAlert size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-ninja-dark dark:text-white mb-1">
                El envío ya salió al courier
              </p>
              <p className="text-text-secondary">
                <b>{dispatched}</b> no tiene stand-by activo, así que el pedido se envió de
                inmediato a su TMS. El cambio quedó aplicado.
              </p>
            </div>
          </div>
          <Button variant="primary" pill onClick={onClose} className="self-end">
            Entendido
          </Button>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
              Courier
            </label>
            <FilterableSelect
              variant="form"
              value={courier}
              onChange={setCourier}
              options={options}
              placeholder={options.length ? 'Selecciona un courier' : emptyLabel}
              disabled={options.length === 0}
            />
            <p className="text-xs text-text-muted mt-2">
              Actual: {order.delivery?.shipping_zone?.courier || 'sin asignar'}
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button
              variant="subtle"
              pill
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleSave}
              loading={saving}
              loadingLabel="Cambiando..."
              disabled={!courier || courier === order.delivery?.shipping_zone?.courier}
            >
              Cambiar courier
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

/**
 * Reassigns the delivery to another courier.
 *
 * A courier without a stand-by window sends the parcel to its TMS the moment
 * the change is accepted, with nothing left to undo — so that outcome is
 * reported back plainly instead of closing the dialog as if nothing happened.
 *
 * Mounted only while open: each opening starts from the order as it is then,
 * and nothing that arrives while it is open overwrites the courier picked.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.order
 * @param {(message: string) => void} props.onError
 */
const ChangeCourierModal = (props) => (props.isOpen ? <ChangeCourierDialog {...props} /> : null);

export default ChangeCourierModal;
